import * as Yup from "yup";
import { DEFAULT_CURRENCY_CODE } from "../constants/defaults";
import {
  DOCUMENT_NOTE_MAX_LENGTH,
  DOCUMENT_NOTE_SPECIAL_CHAR_MESSAGE,
  isAllowedDocumentNote,
} from "../resources/common/DocumentNoteField";
import {createTransactionTypeFields} from "./transactionTypeProfileValidation";

export const CreditNotesValidation = Yup.object({
    ...createTransactionTypeFields(),
  document_number: Yup.string().when("$auto_numbering_enabled", {
    is: false,
    then: (schema) =>
      schema
        .required("Document number is required")
        .test("log-debug", "DEBUG", function (value) {
          return true;
        }),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  issue_date: Yup.date().required("Credit Note date is required"),

  invoice_reference_id: Yup.string()
    .transform((val) => (typeof val === "string" ? val.trim() : val))
    .required("Invoice number is required"),

  invoice_reference_date: Yup.date().required("Invoice date is required"),

  discrepancy_response: Yup.string().required(
    "Credit Note reason code is required",
  ),

  payment_means: Yup.mixed().nullable().notRequired(),

  accounting_supplier_party: Yup.mixed().required("Billed by is required"),

  accounting_customer_party: Yup.mixed().required("Billed to is required"),

  document_lines: Yup.array()
    .of(Yup.object().shape({}))
    .ensure()
    .min(1, "At least one line item is required")
    .required("Line items are required"),

  document_currency_code: Yup.string()
    .transform((val) => (typeof val === "string" ? val.trim() : val))
    .required("Currency code is required"),

  tax_exchange_rate: Yup.object({
    calculation_rate: Yup.number()
      .nullable()
      .transform((_, val) => (val === "" ? null : Number(val)))
      .when("document_currency_code", {
        is: (code) => code !== DEFAULT_CURRENCY_CODE,
        then: (schema) =>
          schema
            .required("Must be a valid number")
            .moreThan(0, "Must be greater than 0"),
        otherwise: (schema) => schema.notRequired(),
      }),
  }).nullable(),

  charges: Yup.array().of(Yup.object().shape({})).ensure().notRequired(),

  allowances: Yup.array().of(Yup.object().shape({})).ensure().notRequired(),

  beneficiary_id: Yup.string().when(
    "transaction_type",
    ([transactionType], schema) => {
      return schema.notRequired().nullable();
    },
  ),

  invoice_period_start_date: Yup.date()
    .nullable()
    .when("transaction_type", {
      is: (transactionType) =>
        typeof transactionType === "string" &&
        transactionType.length === 20 &&
        transactionType.charAt(4) === "1",
      then: (schema) =>
        schema.required(
          "Invoicing period start date is required for Summary Invoice",
        ),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  invoice_period_end_date: Yup.date()
    .nullable()
    .when("transaction_type", {
      is: (transactionType) =>
        typeof transactionType === "string" &&
        transactionType.length === 20 &&
        transactionType.charAt(4) === "1",
      then: (schema) =>
        schema.required(
          "Invoicing period end date is required for Summary Invoice",
        ),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  invoice_period_description: Yup.string()
    .nullable()
    .when("transaction_type", {
      is: (transactionType) =>
        typeof transactionType === "string" &&
        transactionType.length === 20 &&
        transactionType.charAt(4) === "1",
      then: (schema) =>
        schema.required("Frequency of billing is required for Summary Invoice"),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),

  note: Yup.string()
    .nullable()
    .max(
      DOCUMENT_NOTE_MAX_LENGTH,
      `Note must be at most ${DOCUMENT_NOTE_MAX_LENGTH} characters`,
    )
    .test(
      "no-special-characters",
      DOCUMENT_NOTE_SPECIAL_CHAR_MESSAGE,
      isAllowedDocumentNote,
    )
    .when("invoice_period_description", {
      is: (description) => {
        if (typeof description !== "string") {
          return false;
        }

        const normalized = description.trim().toUpperCase();

        return normalized === "OTHERS" || normalized === "OTH";
      },
      then: (schema) =>
        schema.required(
          "Invoice note is mandatory when frequency of billing is Others",
        ),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
});
